/**
 * Currency conversion service using Fixer API
 * Handles fetching exchange rates and converting amounts between currencies
 */

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
const CACHE_KEY = 'currency_rates_cache';

/**
 * Get cached exchange rates
 */
const getCachedRates = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { rates, timestamp } = JSON.parse(cached);
      const now = Date.now();
      // Check if cache is still valid (less than 1 hour old)
      if (now - timestamp < CACHE_DURATION) {
        return rates;
      }
    }
  } catch (error) {
    console.error('Error reading cached rates:', error);
  }
  return null;
};

/**
 * Cache exchange rates
 */
const cacheRates = (rates) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      rates,
      timestamp: Date.now(),
    }));
  } catch (error) {
    console.error('Error caching rates:', error);
  }
};

/**
 * Fetch exchange rates from Fixer API
 */
const fetchExchangeRates = async () => {
  try {
    const response = await fetch('/api/currency/rates');
    if (!response.ok) {
      throw new Error('Failed to fetch exchange rates');
    }
    const data = await response.json();
    return data.rates || {};
  } catch (error) {
    console.error('Error fetching exchange rates:', error);
    return null;
  }
};

/**
 * Get exchange rates (from cache or API)
 */
export const getExchangeRates = async (forceRefresh = false) => {
  // Try cache first if not forcing refresh
  if (!forceRefresh) {
    const cachedRates = getCachedRates();
    if (cachedRates) {
      return cachedRates;
    }
  }

  // Fetch from API
  const rates = await fetchExchangeRates();
  if (rates) {
    cacheRates(rates);
    return rates;
  }

  // Fallback to cached rates even if expired
  const cachedRates = getCachedRates();
  if (cachedRates) {
    return cachedRates;
  }

  return null;
};

/**
 * Convert amount from one currency to another
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency code (e.g., 'EUR')
 * @param {string} toCurrency - Target currency code (e.g., 'CHF')
 * @param {object} rates - Exchange rates object (optional, will fetch if not provided)
 * @returns {Promise<number>} Converted amount
 */
export const convertCurrency = async (amount, fromCurrency, toCurrency, rates = null) => {
  if (fromCurrency === toCurrency) {
    return amount;
  }

  const numAmount = parseFloat(amount || 0);
  if (isNaN(numAmount) || numAmount === 0) {
    return 0;
  }

  // Get rates if not provided
  if (!rates) {
    rates = await getExchangeRates();
  }

  if (!rates) {
    console.warn('Exchange rates not available, returning original amount');
    return numAmount;
  }

  // Fixer API returns rates relative to EUR (base currency)
  // Example: rates.USD = 1.08 means 1 EUR = 1.08 USD
  
  // If fromCurrency is EUR (base)
  if (fromCurrency === 'EUR' && rates[toCurrency]) {
    return Math.round((numAmount * rates[toCurrency]) * 100) / 100;
  }

  // If toCurrency is EUR (base)
  if (toCurrency === 'EUR' && rates[fromCurrency]) {
    return Math.round((numAmount / rates[fromCurrency]) * 100) / 100;
  }

  // Convert between two non-EUR currencies via EUR
  if (rates[fromCurrency] && rates[toCurrency]) {
    // Step 1: Convert from source currency to EUR
    const amountInEUR = numAmount / rates[fromCurrency];
    // Step 2: Convert from EUR to target currency
    const convertedAmount = amountInEUR * rates[toCurrency];
    return Math.round(convertedAmount * 100) / 100; // Round to 2 decimal places
  }

  console.warn(`Exchange rate not found for ${fromCurrency} to ${toCurrency}`);
  return numAmount;
};

/**
 * Convert currency synchronously (uses cached rates)
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency code
 * @param {string} toCurrency - Target currency code
 * @returns {number} Converted amount (or original if rates not available)
 */
export const convertCurrencySync = (amount, fromCurrency, toCurrency) => {
  if (fromCurrency === toCurrency) {
    return amount;
  }

  const numAmount = parseFloat(amount || 0);
  if (isNaN(numAmount) || numAmount === 0) {
    return 0;
  }

  const rates = getCachedRates();
  if (!rates) {
    return numAmount;
  }

  // Fixer API returns rates relative to EUR (base currency)
  // Example: rates.USD = 1.08 means 1 EUR = 1.08 USD
  
  // If fromCurrency is EUR (base)
  if (fromCurrency === 'EUR' && rates[toCurrency]) {
    return Math.round((numAmount * rates[toCurrency]) * 100) / 100;
  }

  // If toCurrency is EUR (base)
  if (toCurrency === 'EUR' && rates[fromCurrency]) {
    return Math.round((numAmount / rates[fromCurrency]) * 100) / 100;
  }

  // Convert between two non-EUR currencies via EUR
  if (rates[fromCurrency] && rates[toCurrency]) {
    // Step 1: Convert from source currency to EUR
    const amountInEUR = numAmount / rates[fromCurrency];
    // Step 2: Convert from EUR to target currency
    const convertedAmount = amountInEUR * rates[toCurrency];
    return Math.round(convertedAmount * 100) / 100;
  }

  return numAmount;
};

